/* eslint-disable no-duplicate-imports */
import type { Texture } from '@pixi/core';
import { Program, Shader, TextureMatrix } from '@pixi/core';
import { Matrix } from '@pixi/math';
import type { Dict } from '@pixi/utils';
import { premultiplyTintToRgba } from '@pixi/utils';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import fragment from './shaders/trail.frag';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import vertex from './shaders/trail.vert';

export interface ITrailMaterialOptions
{
    alpha?: number;
    tint?: number;
    pluginName?: string;
    program?: Program;
    uniforms?: Dict<unknown>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface TrailMaterial extends GlobalMixins.MeshMaterial {}

export class TrailMaterial extends Shader
{
    public readonly uvMatrix: TextureMatrix;

    public batchable: boolean;
    public pluginName?: string;

    _tintRGB!: number;

    private _colorDirty: boolean;
    private _alpha!: number;
    private _tint!: number;

    constructor(uSampler: Texture, options?: ITrailMaterialOptions)
    {
        const uniforms = {
            uSampler,
            alpha: 1,
            uTextureMatrix: Matrix.IDENTITY,
            uColor: new Float32Array([1, 1, 1, 1]),
            uOffset: 0,
        };

        // Set defaults
        options = Object.assign({
            tint: 0xFFFFFF,
            alpha: 1,
            pluginName: 'batch',
        }, options);

        if (options.uniforms)
        {
            Object.assign(uniforms, options.uniforms);
        }

        super(options.program || Program.from(vertex, fragment), uniforms);

        this._colorDirty = false;

        this.uvMatrix = new TextureMatrix(uSampler);

        // this.batchable = options.program === undefined;
        this.batchable = true;

        this.pluginName = options.pluginName;
        this.tint = options.tint ?? 0xFFFFFF;
        this.alpha = options.alpha ?? 1;
    }

    get texture(): Texture
    {
        return this.uniforms.uSampler;
    }

    set texture(value: Texture)
    {
        if (this.uniforms.uSampler !== value)
        {
            this.uniforms.uSampler = value;
            this.uvMatrix.texture = value;
        }
    }

    set alpha(value: number)
    {
        if (value === this._alpha) return;

        this._alpha = value;
        this._colorDirty = true;
    }

    get alpha(): number
    {
        return this._alpha;
    }

    set tint(value: number)
    {
        if (value === this._tint) return;

        this._tint = value;
        this._tintRGB = (value >> 16) + (value & 0xff00) + ((value & 0xff) << 16);
        this._colorDirty = true;
    }

    get tint(): number
    {
        return this._tint;
    }

    get offset(): number
    {
        return this.uniforms.uOffset;
    }

    set offset(v: number)
    {
        this.uniforms.uOffset = v % this.texture.width;
    }

    public update(): void
    {
        if (this._colorDirty)
        {
            this._colorDirty = false;
            const baseTexture = this.texture.baseTexture;

            premultiplyTintToRgba(
                this._tint, this._alpha, this.uniforms.uColor, (baseTexture.alphaMode as unknown as boolean),
            );
        }
        if (this.uvMatrix.update())
        {
            this.uniforms.uTextureMatrix = this.uvMatrix.mapCoord;
        }
    }
}
